import { getSafe, getUrl } from "@/utils";
import { gzipcompress, gzipdecompress } from "./gZipUtils";
import { signOut } from "next-auth/react";

const apiEncryption = async (
  data: any,
  mode: string,
  system: string,
  isBaseURL: boolean = false,
) => {
  const basePath =
    process.env.NEXT_PUBLIC_PATH_URL !== ""
      ? `/${process.env.NEXT_PUBLIC_PATH_URL}`
      : "";
  const encryptionUrl =
    typeof window !== "undefined"
      ? `${basePath}/api/encryption`
      : `${isBaseURL ? process.env.NEXTAUTH_PUBLIC_BASEURL : ""}${basePath}/api/encryption`;

  return await fetch(encryptionUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ data: data, mode: mode, system: system }),
  })
    .then(async (res) => {
      const response = await res.json();

      if (response === null || response === undefined) {
        throw new Error(
          `${mode} failed, Please checking your private key and public key.`,
        );
      }

      if (res.status === 200) {
        return {
          ...response,
          isEncryptionFailed: false,
        };
      } else {
        return {
          ...response,
          isEncryptionFailed: true,
          code: getSafe(() => response.code) || "APP-API-ENCRYPTION-00-00",
          msg:
            getSafe(() => response.msg) ||
            "Encryption or Decryption Failed. Please checking your private keys and public keys.",
        };
      }
    })
    .catch((error) => {
      return {
        error,
        code: "APP-API-ENCRYPTION-00-00",
        msg:
          error.message ||
          "Encryption or Decryption Failed. Please checking your private keys and public keys.",
        isEncryptionFailed: true,
      };
    });
};

export const requestGet = async (
  apiPath: string,
  requestType: string = "master",
  token: string | null = null,
  params: any | null = null,
  headers: any | null = {},
  body?: any | null,
) => {
  try {
    const applicationKey = gzipcompress(
      `${process.env.NEXT_PUBLIC_API_APP_KEY}`,
    );
    const getParams = getSafe(() => new URLSearchParams(params));
    const Authorization = `Bearer ${token}`;
    const optHeaders = {
      ...headers,
      "Content-Type": "application/json",
      "X-Application": `${process.env.NEXT_PUBLIC_API_APP_SECRET_KEY}`,
      "application-key": applicationKey,
    };

    let encryptCertKey = process.env.NEXT_PUBLIC_APP_ENCRYPT_FOLDER!;
    let requestUrl = process.env.NEXT_PUBLIC_API_APP_URL;
    if (requestType === "ssp") {
      requestUrl = process.env.NEXT_PUBLIC_API_APP_URL;
      encryptCertKey = process.env.NEXT_PUBLIC_APP_ENCRYPT_FOLDER!;
    } else if (requestType === "bondfeature") {
      requestUrl = process.env.NEXT_PUBLIC_API_APP_BOND_FEATURE_URL;
      encryptCertKey = process.env.NEXT_PUBLIC_APP_ENCRYPT_FOLDER!;
    } else if (requestType === "master") {
      requestUrl = process.env.NEXT_PUBLIC_API_APP_MASTER_DATA_URL;
      encryptCertKey = process.env.NEXT_PUBLIC_MASTERDATA_ENCRYPT_FOLDER!;
    }

    const requestOptions = {
      method: "GET",
      headers: token ? { ...optHeaders, Authorization } : optHeaders,
    };

    //Request API
    const res = await fetch(
      `${requestUrl}/${apiPath}${params ? `?${getParams}` : ""}`,
      {
        ...requestOptions,
        ...(body && { body: body }),
      },
    );

    //Get API Response
    let resJson = await res.json().then((res) => res);

    //Decrypt data
    if (
      res.status === 200 &&
      resJson.data &&
      resJson.data.secretCode !== undefined &&
      resJson.data.encryptData !== undefined
    ) {
      resJson = await apiEncryption(resJson.data, "decrypt", encryptCertKey);

      if (resJson.isEncryptionFailed) {
        return { ...resJson };
      }

      //Decompress Gzip data
      const jsonResDecompress: any = gzipdecompress(resJson.data);

      return { data: jsonResDecompress, status: res.status };
    } else if (res.status === 401) {
      const lang = window.location.pathname.split("/").includes("th")
        ? "th"
        : "en";
      window.location.href = getUrl(lang, "signout", true);
      setTimeout(() => {
        return { ...resJson, status: res.status };
      }, 2000);
    }

    //Return API Response
    return { ...resJson, status: res.status };
  } catch (error) {
    if (!body) {
      return {
        error,
        code: "APP-API-GET-CONNECTION-00-01",
        msg: `Please checking API Request ${apiPath}.`,
      };
    }
    return {
      error,
      code: "APP-API-GET-CONNECTION-00-00",
      msg: "Connection Failed or Timeout.",
    };
  }
};

export const requestPost = async (
  apiPath: string,
  requestType: string = "ssp",
  token: string | null = null,
  body: any | null = null,
  headers: any = {},
) => {
  try {
    const applicationKey = gzipcompress(
      `${process.env.NEXT_PUBLIC_API_APP_KEY}`,
    );
    const Authorization = `Bearer ${token}`;
    const optHeaders = {
      ...headers,
      "Content-Type": "text/plain",
      "X-Application": `${process.env.NEXT_PUBLIC_API_APP_SECRET_KEY}`,
      "application-key": applicationKey,
    };

    let encryptCertKey = process.env.NEXT_PUBLIC_APP_ENCRYPT_FOLDER!;
    let requestUrl = process.env.NEXT_PUBLIC_API_APP_URL;
    if (requestType === "ssp") {
      requestUrl = process.env.NEXT_PUBLIC_API_APP_URL;
      encryptCertKey = process.env.NEXT_PUBLIC_APP_ENCRYPT_FOLDER!;
    } else if (requestType === "bondfeature") {
      requestUrl = process.env.NEXT_PUBLIC_API_APP_BOND_FEATURE_URL;
      encryptCertKey = process.env.NEXT_PUBLIC_APP_ENCRYPT_FOLDER!;
    } else if (requestType === "master") {
      requestUrl = process.env.NEXT_PUBLIC_API_APP_MASTER_DATA_URL;
      encryptCertKey = process.env.NEXT_PUBLIC_MASTERDATA_ENCRYPT_FOLDER!;
    }

    //Encrypt data
    let bodyEncrypted = null;
    if (body !== null && body !== undefined) {
      bodyEncrypted = await apiEncryption(body, "encrypt", encryptCertKey);

      if (bodyEncrypted.isEncryptionFailed) {
        return { ...bodyEncrypted };
      }
    }

    //Request API
    const res = await fetch(`${requestUrl}/${apiPath}`, {
      method: "POST",
      headers: token ? { ...optHeaders, Authorization } : optHeaders,
      body: JSON.stringify(
        requestType === "ssp" || requestType === "bondfeature"
          ? bodyEncrypted
          : bodyEncrypted.data,
      ),
    });

    //Get API Response
    let resJson = (await res.json()) || res;

    //Decrypt data
    if (
      res.status === 200 &&
      resJson.data &&
      resJson.data.secretCode !== undefined &&
      resJson.data.encryptData !== undefined
    ) {
      resJson = await apiEncryption(resJson.data, "decrypt", encryptCertKey);

      if (resJson.isEncryptionFailed) {
        return { ...resJson };
      }

      //Decompress Gzip data
      const jsonResDecompress: any = gzipdecompress(resJson.data);

      return { data: jsonResDecompress, status: res.status };
    } else if (res.status === 401) {
      const lang = window.location.pathname.split("/").includes("th")
        ? "th"
        : "en";
      window.location.href = getUrl(lang, "signout", true);

      setTimeout(() => {
        return { ...resJson, status: res.status };
      }, 2000);
    }

    //Return API Response
    return { ...resJson, status: res.status };
  } catch (error) {
    if (!body) {
      return {
        error,
        code: "APP-API-POST-CONNECTION-00-01",
        msg: `Please checking API Request ${apiPath}.`,
      };
    }
    return {
      error,
      code: "APP-API-POST-CONNECTION-00-00",
      msg: "Connection Failed or Timeout.",
    };
  }
};

export const requestAuthGet = async (
  apiPath: string,
  token: string = "",
  params: any = undefined,
  headers: any = {},
) => {
  try {
    let encryptCertKey = "secure";
    const applicationKey = gzipcompress(
      `${process.env.NEXT_PUBLIC_API_APP_KEY}`,
    );
    let authHeaders = headers;
    if (token !== "") {
      authHeaders["Authorization"] = `Bearer ${token}`;
    }
    const getParams = getSafe(() => new URLSearchParams(params));
    const res = await fetch(
      `${process.env.NEXT_PUBLIC_API_AUTH_URL}/${apiPath}${
        params ? `?${getParams}` : ""
      }`,
      {
        method: "GET",
        headers: {
          ...authHeaders,
          "Content-Type": "application/json",
          "application-key": applicationKey,
          "X-Authentication": `${process.env.NEXT_PUBLIC_API_AUTH_SECRET_KEY}`,
        },
      },
    );
    //Get API Response
    let resJson = (await res.json()) || res;

    //Decrypt data
    if (
      res.status === 200 &&
      resJson.data &&
      resJson.data.secretCode !== undefined &&
      resJson.data.encryptData !== undefined
    ) {
      resJson = await apiEncryption(
        resJson.data,
        "decrypt",
        encryptCertKey,
        false,
      );

      if (resJson.isEncryptionFailed) {
        return { ...resJson };
      }

      //Decompress Gzip data
      const jsonResDecompress: any = gzipdecompress(resJson.data);

      return { data: jsonResDecompress, status: res.status };
    }

    //Return API Response
    return { ...resJson, status: res.status };
  } catch (error) {
    return {
      error,
      code: "APP-AUTH-GET-CONNECTION-00-00",
      msg: "Connection Failed or Timeout.",
    };
  }
};

export const requestAuthPost = async (
  apiPath: string,
  requestType: string = "ssp",
  token: string | null = null,
  body: any | null = null,
  headers: any = {},
  isBaseURL: boolean = true,
) => {
  try {
    const applicationKey = gzipcompress(
      `${process.env.NEXT_PUBLIC_API_SECURITY_APP_KEY}`,
    );

    let encryptCertKey = process.env.NEXT_PUBLIC_SECURITY_ENCRYPT_FOLDER!;
    let requestUrl = process.env.NEXT_PUBLIC_API_AUTH_URL;

    const Authorization = `Bearer ${token}`;
    const optHeaders = {
      ...headers,
      "Content-Type": "text/plain",
      "X-Application": `${process.env.NEXT_PUBLIC_API_APP_SECRET_KEY}`,
      "application-key": applicationKey,
    };

    //Encrypt data
    let bodyEncrypted = null;
    if (body !== null && body !== undefined) {
      bodyEncrypted = await apiEncryption(
        body,
        "encrypt",
        encryptCertKey,
        isBaseURL,
      );

      if (bodyEncrypted.isEncryptionFailed) {
        return { ...bodyEncrypted };
      }
    }

    //Request API
    const res = await fetch(`${requestUrl}/${apiPath}`, {
      method: "POST",
      headers: token ? { ...optHeaders, Authorization } : optHeaders,
      body: JSON.stringify(bodyEncrypted.data),
    });

    //Get API Response
    let resJson = (await res.json()) || res;

    //Decrypt data
    if (
      res.status === 200 &&
      resJson.data &&
      resJson.data.secretCode !== undefined &&
      resJson.data.encryptData !== undefined
    ) {
      resJson = await apiEncryption(
        resJson.data,
        "decrypt",
        encryptCertKey,
        isBaseURL,
      );

      if (resJson.isEncryptionFailed) {
        return { ...resJson };
      }

      //Decompress Gzip data
      const jsonResDecompress: any = gzipdecompress(resJson.data);

      return { data: jsonResDecompress, status: res.status };
    }

    //Return API Response
    return { ...resJson, status: res.status };
  } catch (error) {
    return {
      error,
      code: "APP-AUTH-POST-CONNECTION-00-00",
      msg: "Connection Failed or Timeout.",
    };
  }
};

export const requestAuthLogin = async (credentials: any, body: any = {}) => {
  try {
    const applicationKey = gzipcompress(
      `${process.env.NEXT_PUBLIC_API_APP_KEY}`,
    );

    let encryptCertKey = process.env.NEXT_PUBLIC_SECURITY_ENCRYPT_FOLDER!;
    let requestUrl = process.env.NEXT_PUBLIC_API_AUTH_URL;

    //Encrypt data
    let bodyEncrypted = null;
    if (credentials.username && credentials.password) {
      bodyEncrypted = await apiEncryption(
        JSON.stringify({
          username: credentials.username,
          password: credentials.password,
        }),
        "encrypt",
        encryptCertKey,
        true,
      );

      if (bodyEncrypted.isEncryptionFailed) {
        return { ...bodyEncrypted };
      }
    }

    const res = await fetch(`${requestUrl}/signin`, {
      method: "POST",
      headers: {
        "Content-Type": "text/plain",
        "application-key": applicationKey,
        "client-key": `${process.env.NEXT_PUBLIC_API_SECURITY_CLIENT_KEY}`,
      },
      body: JSON.stringify(bodyEncrypted.data),
    });

    //Get API Response
    let resJson = (await res.json()) || res;

    //Decrypt data
    if (
      res.status === 200 &&
      resJson.data &&
      resJson.data.secretCode !== undefined &&
      resJson.data.encryptData !== undefined
    ) {
      resJson = await apiEncryption(
        resJson.data,
        "decrypt",
        encryptCertKey,
        true,
      );

      if (resJson.isEncryptionFailed) {
        return { ...resJson };
      }

      //Decompress Gzip data
      const jsonResDecompress: any = JSON.parse(gzipdecompress(resJson.data));

      if (resJson.data && jsonResDecompress.token) {
        const sessionResult = {
          ...jsonResDecompress,
          status: res.status,
        };
        return sessionResult;
      }
    }

    return { ...resJson, status: res.status };
  } catch (error) {
    return {
      error,
      code: "APP-AUTHLOGIN-CONNECTION-00-00",
      msg: "Connection Failed or Timeout.",
    };
  }
};

export const requestAuthAccessToken = async (
  credentials: any,
  body: any = {},
) => {
  try {
    const applicationKey = gzipcompress(
      `${process.env.NEXT_PUBLIC_API_APP_KEY}`,
    );

    let encryptCertKey = process.env.NEXT_PUBLIC_SECURITY_ENCRYPT_FOLDER!;
    let requestUrl = process.env.NEXT_PUBLIC_API_AUTH_URL;

    //Encrypt data
    let bodyEncrypted = null;
    if (credentials.clientId && credentials.clientSecret) {
      bodyEncrypted = await apiEncryption(
        JSON.stringify({
          clientId: credentials.clientId,
          clientSecret: credentials.clientSecret,
        }),
        "encrypt",
        encryptCertKey,
        true,
      );

      if (bodyEncrypted.isEncryptionFailed) {
        return { ...bodyEncrypted };
      }
    }

    const res = await fetch(`${requestUrl}/accesstoken`, {
      method: "POST",
      headers: {
        "Content-Type": "text/plain",
        "application-key": applicationKey,
      },
      body: JSON.stringify(bodyEncrypted.data),
    });

    //Get API Response
    let resJson = (await res.json()) || res;

    //Decrypt data
    if (
      res.status === 200 &&
      resJson.data &&
      resJson.data.secretCode !== undefined &&
      resJson.data.encryptData !== undefined
    ) {
      resJson = await apiEncryption(
        resJson.data,
        "decrypt",
        encryptCertKey,
        true,
      );

      if (resJson.isEncryptionFailed) {
        return { ...resJson };
      }

      //Decompress Gzip data
      const jsonResDecompress: any = JSON.parse(gzipdecompress(resJson.data));

      if (resJson.data && jsonResDecompress.token) {
        const sessionResult = {
          ...jsonResDecompress,
          status: res.status,
        };
        return sessionResult;
      }
    }

    return { ...resJson, status: res.status };
  } catch (error: any) {
    return {
      error,
      code: "APP-AUTH-ACCESSTOKEN-CONNECTION-00-00",
      msg: `Connection Failed or Timeout. (${error.message})`,
    };
  }
};
